/**
 * Session Expiry Handler
 *
 * Centralized handling of session timeout/expiry across all API calls.
 * When a 401 with session expired message is detected, this handler
 * will sign out the user and redirect to login with a friendly message.
 */

import { auth } from "@/config/firebase";
import { signOut as firebaseSignOut } from "firebase/auth";
import { toast } from "sonner";

// Track if we're already handling a session expiry to prevent multiple redirects
let isHandlingExpiry = false;

/**
 * Check if an error response indicates a session expiry
 */
export function isSessionExpiredError(
  status: number,
  errorMessage?: string
): boolean {
  if (status !== 401) return false;

  // Check for session timeout specific messages
  const sessionExpiredPatterns = [
    "session expired",
    "session timeout",
    "session has expired",
    "token expired",
    "lastActivityAt",
  ];

  if (errorMessage) {
    const lowerMessage = errorMessage.toLowerCase();
    return sessionExpiredPatterns.some((pattern) =>
      lowerMessage.includes(pattern.toLowerCase())
    );
  }

  return false;
}

/**
 * Handle session expiry - sign out user and redirect to login
 */
export async function handleSessionExpiry(): Promise<void> {
  // Prevent multiple simultaneous handling
  if (isHandlingExpiry) return;
  isHandlingExpiry = true;

  // Diagnostic: log the call site so future false-positives are traceable.
  // Sentry breadcrumb collects this automatically via console.error.
  console.error(
    "[session-handler] handleSessionExpiry fired — stack:",
    new Error("trace").stack,
  );

  try {
    // Show user-friendly message
    toast.error("Your session has expired. Please sign in again.", {
      duration: 5000,
      id: "session-expired", // Prevent duplicate toasts
    });

    // Clear session storage
    sessionStorage.removeItem("currentOrganizationId");

    // Sign out from Firebase
    await firebaseSignOut(auth);

    // Redirect to login after a brief delay to show the toast
    setTimeout(() => {
      window.location.href = "/login";
    }, 1000);
  } catch (error) {
    console.error("Error handling session expiry:", error);
    // Force redirect even if sign out fails
    window.location.href = "/login";
  } finally {
    // Reset after a delay to allow for redirect
    setTimeout(() => {
      isHandlingExpiry = false;
    }, 2000);
  }
}

/**
 * Check if an error response indicates a missing org API key (BYOK enforcement)
 */
export function isOrgApiKeyRequiredError(
  status: number,
  errorCode?: string
): boolean {
  return status === 403 && errorCode === "ORG_API_KEY_REQUIRED";
}

// Track if we're already showing the API key error to prevent duplicate toasts
let isShowingApiKeyError = false;

/**
 * Handle missing org API key - show toast with link to settings
 */
export function handleOrgApiKeyRequired(): void {
  if (isShowingApiKeyError) return;
  isShowingApiKeyError = true;

  toast.error(
    "Your organization needs an API key to use AI features. Go to Settings > Provider Settings to add your API key.",
    {
      duration: 8000,
      id: "org-api-key-required",
      action: {
        label: "Go to Settings",
        onClick: () => {
          window.location.href = "/settings?tab=access-security";
        },
      },
    }
  );

  setTimeout(() => {
    isShowingApiKeyError = false;
  }, 3000);
}

/**
 * Wrapper for fetch that handles session expiry
 * Use this for non-GraphQL API calls
 */
export async function fetchWithSessionHandler(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  const response = await fetch(input, init);

  if (response.status === 401) {
    // Try to parse error message
    const clonedResponse = response.clone();
    try {
      const data = await clonedResponse.json();
      const errorMessage =
        data.error || data.message || JSON.stringify(data);

      if (isSessionExpiredError(401, errorMessage)) {
        await handleSessionExpiry();
        throw new Error("Session expired");
      }
    } catch (parseError) {
      // If we can't parse, check response text
      const text = await response.clone().text();
      if (isSessionExpiredError(401, text)) {
        await handleSessionExpiry();
        throw new Error("Session expired");
      }
    }
  }

  return response;
}

/**
 * Check a GraphQL response for session expiry errors
 */
export function checkGraphQLResponseForSessionExpiry(response: unknown): void {
  if (!response || typeof response !== "object") return;

  const resp = response as { status?: number; errors?: Array<{ message?: string }> };

  // Check for 401 status
  if (resp.status === 401) {
    handleSessionExpiry();
    return;
  }

  // Check for session expired in GraphQL errors
  if (resp.errors && Array.isArray(resp.errors)) {
    for (const error of resp.errors) {
      if (error.message && isSessionExpiredError(401, error.message)) {
        handleSessionExpiry();
        return;
      }
    }
  }
}
